// Quick entry form for meal events
import React, { useState } from 'react';
import { healthAPI } from '../api';
import { MealMetadata, LocationData } from '../types';
import { LocationInput } from './LocationInput';
import './QuickEntry.css';

interface MealFormProps {
  onSuccess?: (traceId: string, warnings?: string[]) => void;
  onError?: (error: string) => void;
  onViewTrace?: (traceId: string) => void;
}

export const MealForm: React.FC<MealFormProps> = ({ onSuccess, onError, onViewTrace }) => {
  const [description, setDescription] = useState<string>('');
  const [mealType, setMealType] = useState<'breakfast' | 'lunch' | 'dinner' | 'snack'>('snack');
  const [portionSize, setPortionSize] = useState<'small' | 'normal' | 'large'>('normal');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [location, setLocation] = useState<LocationData | undefined>(undefined);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!description.trim()) {
      if (onError) onError('Please describe your meal');
      return;
    }

    setIsSubmitting(true);

    try {
      const data: MealMetadata = {
        foods: [description],
        meal_type: mealType,
        location: location,
        notes: description
      };

      const requestBody: any = {
        type: 'meal',
        data: {
          ...data,
          portion_size: portionSize,
        } as any,
      };

      const response = await healthAPI.createEvent(requestBody);

      // Trigger analysis immediately (fire and forget)
      healthAPI.analyzeEvent(response.trace_id).catch(err => console.error("Analysis trigger failed", err));

      // Reset form
      setDescription('');
      setPortionSize('normal');
      setLocation(undefined);

      // Successfully submitted

      if (onSuccess) {
        onSuccess(response.trace_id, response.warnings);
      }

      if (onViewTrace) {
        onViewTrace(response.trace_id);
      }
    } catch (error) {
      console.error('Error logging meal:', error);
      if (onError) {
        onError(error instanceof Error ? error.message : 'Unknown error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Auto-suggest meal type based on time
  const getCurrentMealType = (): 'breakfast' | 'lunch' | 'dinner' | 'snack' => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 11) return 'breakfast';
    if (hour >= 11 && hour < 16) return 'lunch';
    if (hour >= 16 && hour < 22) return 'dinner';
    return 'snack';
  };

  React.useEffect(() => {
    setMealType(getCurrentMealType());
  }, []);

  return (
    <form onSubmit={handleSubmit} className="quick-entry-form meal-form">
      <h3>🍽️ Meal Entry</h3>

      <div className="form-group">
        <label>Meal Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="E.g. 'Pizza and salad at Joe's, yesterday 7pm'"
          className="food-input"
          style={{ width: '100%' }}
          rows={3}
        />
        <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '4px' }}>
          Describe what you ate, where, and when. AI will extract the details.
        </div>
      </div>

      <div className="form-group">
        <label>Meal Type</label>
        <div className="meal-type-buttons">
          {(['breakfast', 'lunch', 'dinner', 'snack'] as const).map((type) => (
            <button
              key={type}
              type="button"
              className={`meal-type-button ${mealType === type ? 'active' : ''}`}
              onClick={() => setMealType(type)}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="form-group">
        <label>Portion Size</label>
        <div className="portion-buttons">
          {(['small', 'normal', 'large'] as const).map((size) => (
            <button
              key={size}
              type="button"
              className={`portion-button ${portionSize === size ? 'active' : ''}`}
              onClick={() => setPortionSize(size)}
            >
              {size.charAt(0).toUpperCase() + size.slice(1)}
            </button>
          ))}
        </div>
      </div>



      <LocationInput onLocationChange={setLocation} initialLocation={location} />

      <button type="submit" className="submit-button" disabled={isSubmitting || !description.trim()}>
        {isSubmitting ? 'Logging...' : 'Log Meal'}
      </button>
    </form>
  );
};
